import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { ReactComponent as TrophyIcon } from '../assets/icons/message/trophy_icon.svg';
import RankBadge from './RankBadge';
import { SkillObject } from '../types';

interface GetSkillCelebrationProps {
  /** 表示対象の未読スキル（空なら何も描画しない） */
  skills: SkillObject[];
  /** 閉じる時のハンドラ */
  onClose: () => void;
}

/**
 * GET SKILL お祝い演出ポップアップ（設計書 §5.3 / §6.3 p.21 / 付録C.5、課題87で仕様変更）。
 *
 * 新規付与された未読スキル（seen_at IS NULL）を、トロフィー + 紫グラデ背景で祝う演出。
 * 課題87の決定により「ページ遷移」ではなく、開いている画面（求人詳細など）の上に重なる
 * オーバーレイ形式のポップアップとして表示する。閉じる導線は「閉じる」のみ（旧「求人一覧へ移動する」
 * のような画面遷移は行わない）。これにより mark-seen 完了前に遷移するレースも解消される。
 *
 * 「1回だけ表示」の冪等性は呼び出し側（useGetSkillCelebration）が mark-seen を呼ぶことで担保する
 * （サーバ側 seen_at の冪等更新 + セッションガード）。
 *
 * /scout と /mypage の双方から再利用できるよう、ScoutPage 内のローカル実装から切り出した共有部品。
 */
const GetSkillCelebration: React.FC<GetSkillCelebrationProps> = ({ skills, onClose }) => {
  if (skills.length === 0) return null;
  return (
    <Backdrop
      open
      onClick={onClose}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        // 背後の画面（求人詳細など）を暗転させて透過させる（全画面テイクオーバーにしない）
        bgcolor: 'rgba(0, 0, 0, 0.6)',
        px: 2,
      }}
    >
      {/* ポップアップカード（付録C.5: 上側 青紫 → 下側 マゼンタ紫の対角グラデ） */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 360,
          maxHeight: '85vh',
          overflowY: 'auto',
          borderRadius: 4,
          background: 'linear-gradient(45deg, #7e318e 0%, #534dff 100%)',
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 4,
          px: 2.5,
        }}
      >
      {/* 右上の閉じるボタン */}
      <IconButton
        onClick={onClose}
        aria-label="閉じる"
        sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(255,255,255,0.9)' }}
      >
        <CloseIcon />
      </IconButton>

      {/* GET SKILL! タイトル */}
      <Typography
        sx={{
          color: '#fff',
          fontWeight: 800,
          fontSize: '34px',
          letterSpacing: 3,
          lineHeight: 1.1,
          textAlign: 'center',
          mb: 2.5,
          mt: 1,
        }}
      >
        GET SKILL!
      </Typography>

      {/* アクセント: 橙〜金のトロフィー（付録C.5 / trophy_icon.svg） */}
      <Box sx={{ mb: 2.5 }}>
        <TrophyIcon width={140} height={140} />
      </Box>

      {/* スキルカード */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          width: '100%',
          maxWidth: 300,
          mb: 3,
        }}
      >
        {skills.map((sk) => {
          return (
            <Box
              key={sk.skill_id}
              sx={{
                bgcolor: '#fff',
                borderRadius: 2.5,
                px: 2,
                py: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '16px', color: '#1a1a1a' }} noWrap>
                  {sk.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RankBadge tier={sk.tier ?? null} size={16} labelFontSize="11px" />
                  <Typography sx={{ fontSize: '11px', color: '#9e9e9e' }}>Lv.{sk.level}</Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* 祝福コピー（p.21 デザイン準拠） */}
      <Typography
        sx={{
          color: '#fff',
          fontWeight: 700,
          fontSize: '22px',
          textAlign: 'center',
          lineHeight: 1.9,
          mb: 3,
        }}
      >
        おめでとうございます！
        <br />
        あなたは新たな
        <br />
        スキルをゲットしました
      </Typography>

      {/* ボタン（閉じるのみ・画面遷移しない） */}
      <Button
        onClick={onClose}
        variant="contained"
        disableElevation
        sx={{
          width: '100%',
          maxWidth: 300,
          borderRadius: 2.5,
          py: 1.25,
          fontWeight: 700,
          fontSize: '14px',
          bgcolor: '#fff',
          color: '#3c3c3c',
          '&:hover': { bgcolor: '#f0f0f0' },
        }}
      >
        閉じる
      </Button>
      </Box>
    </Backdrop>
  );
};

export default GetSkillCelebration;
